import {
  BTypeMainHead,
  BTypeCardView,
  FUNERAL_SIDO,
  FUNERAL_SIGUNGU,
  BTypeBeneficiaryCTA,
  colors,
  CTypeMap,
  FullWidthButton,
  ATypeNationalMeritDescription,
  FullWidthTwinButton,
  GOI_WEB_URL,
  ATypeNationalMeritSummary,
  customEvent,
  useCrematoriumInquiryModal,
  getCrematoriumPhoneByCompanyname,
} from "goi_common";
import { GetStaticPaths, GetStaticProps } from "next";

import React from "react";
import { ParlorDetailProps } from "goi_common";
import { parlorRegion } from "@/constants/parlorRegion";
import { Box, Stack } from "@mui/material";
import Head from "next/head";

import crematoriums from "goi_common/src/constants/hwajang_detail.json";

const { regionColor, targetSidos } = parlorRegion;

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = crematoriums
    .filter((p) => targetSidos.includes(String(p.sido)))
    .map((crema) => ({
      params: {
        companyname: crema.companyname + " 소개",
      },
    }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const code = params?.companyname as string;
  const companyname = code.replace(" 소개", "");
  let crematorium;
  let district;
  let name = "";
  let description = "";

  if (companyname) {
    crematorium = crematoriums.find(
      (crema) => crema.companyname === companyname
    );
    if (crematorium) {
      name = crematorium.companyname;

      district = crematorium.address.slice(0, 2);
      if (crematorium.address.split(" ")[0].length == 4)
        district = crematorium.address[0] + crematorium.address[2];

      description = `${crematorium.companyname} ${crematorium.address} 국가유공자 혜택 화장장 정보, ${crematorium.companyname} ${crematorium.address} 국가유공자 화장 비용 안내, ${crematorium.companyname} ${crematorium.address} 국가유공자 화장 비용 `;
    }
  }

  return {
    props: {
      defaultCrema: crematorium,
      pageDescription: description,
      pageKeywords: `${crematorium?.companyname}, ${crematorium?.companyname} 국가유공자, ${district} 국가유공자 장례, ${district} 국가유공자 장례 비용, ${district} 국가유공자 화장 비용, ${district} 국가유공자 화장장, ${district} 국가유공자 장례 혜택`,
      pageOgTitle: `${crematorium?.companyname} 국가유공자 혜택 화장장 소개 및 비용 정보:: 부산울산경남장례정보포털`,
      pageOgDescription: description,
      pageOgImage: crematorium?.images[0].url || "",
      hasError: !crematorium,
    },
  };
};

export default function NationalMeritCrematoriumPage({
  defaultCrema,
  pageOgTitle,
}: {
  defaultCrema: ParlorDetailProps;
  pageOgTitle: string;
}) {
  const regionCrematoriums = crematoriums.filter((p) =>
    targetSidos.includes(String(p.sido))
  );

  /** 문의 모달의 개장·이장 / 고인 확인·방문이 거는 번호. 원본에 없으면 null 이고 고이로 폴백한다. */
  const facilityPhone = getCrematoriumPhoneByCompanyname(
    defaultCrema.companyname
  );
  const { openInquiryModal } = useCrematoriumInquiryModal({
    eventDomain: "gyeongsang",
    eventPath: "nat_fr-crema",
  });
  const sidoName = FUNERAL_SIDO.get(Number(defaultCrema.sido));
  const sigunguName = FUNERAL_SIGUNGU.get(Number(defaultCrema.sido))?.get(
    Number(defaultCrema.sigungu)
  );
  const carouselItems = regionCrematoriums.map((res, i) => {
    return {
      "@type": "ListItem",
      name: `(비용안내) ${res.companyname}`,
      image: `${res.images[0].url}`,
      url: `https://www.gyeongsang-portal.co.kr/national/free-crema/${res.companyname} 소개/`,
      position: i + 1,
    };
  });

  const carouselRichSearch =
    regionCrematoriums.length > 6
      ? {
          "@context": "http://schema.org",
          "@type": "ItemList",
          itemListElement: carouselItems,
        }
      : {};
  return (
    <Stack>
      <Head>
        <title>{pageOgTitle}</title>
        {regionCrematoriums.length > 5 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(carouselRichSearch),
            }}
          />
        )}
      </Head>

      <BTypeMainHead
        sidoName={sidoName}
        sigunguName={sigunguName}
        mainColor={regionColor}
        defaultParlor={defaultCrema}
        title={`${defaultCrema.companyname}`}
        managed_type={defaultCrema.managed_type}
        isHwajang
        event={{
          domain: "gyeongsang",
          path: "nat_fr-crema",
          trigger_and_target: "br_crumb_crema",
        }}
      />
      <Stack
        margin="20px 12px"
        padding="24px 16px"
        style={{
          background: "white",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
        gap="12px"
      >
        <Box className="subtitle_21_b">
          {defaultCrema.companyname}
          <br />
          국가유공자 장례 준비
        </Box>
        <ATypeNationalMeritDescription />
        <Box className="subtitle_14_b" textAlign="center">
          상담을 통해 자세한 국가 유공자 장례 혜택을 안내드립니다.
        </Box>
        <FullWidthTwinButton
          firstBtn={{
            isGoiCall: true,
            text: "상담하기",
            onClick: () => {
              openInquiryModal(facilityPhone);
              customEvent({
                domain: "gyeongsang",
                path: "nat_fr-crema",
                trigger_and_target: "goi_call",
              });
            },
          }}
          secondBtn={{
            text: "비용 알아보기",
            href: `${GOI_WEB_URL}?utm_source=dogsound` + "/match/request/?utm_source=dogsound",
            onClick: () => {
              customEvent({
                domain: "gyeongsang",
                path: "nat_fr-crema",
                trigger_and_target: "uv_mth_req",
              });
            },
          }}
          backgroundColor={regionColor.c400}
        />
      </Stack>

      <Stack
        margin="20px 12px"
        padding="24px 16px"
        style={{
          background: "white",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
        gap="12px"
      >
        <ATypeNationalMeritSummary mainColor={regionColor} />
        <Box className="subtitle_14_b" textAlign="center">
          상담을 통해 자세한 국가 유공자 장례 혜택을 안내드립니다.
        </Box>
        <FullWidthTwinButton
          firstBtn={{
            isGoiCall: true,
            text: "상담하기",
            onClick: () => {
              openInquiryModal(facilityPhone);
              customEvent({
                domain: "gyeongsang",
                path: "nat_fr-crema",
                trigger_and_target: "goi_call_btom",
              });
            },
          }}
          secondBtn={{
            text: "비용 알아보기",
            href: `${GOI_WEB_URL}?utm_source=dogsound` + "/match/request/?utm_source=dogsound",
            onClick: () => {
              customEvent({
                domain: "gyeongsang",
                path: "nat_fr-crema",
                trigger_and_target: "uv_mth_req_btom",
              });
            },
          }}
          backgroundColor={regionColor.c400}
        />
      </Stack>

      <Stack
        margin="20px 12px"
        padding="24px 16px 20px"
        style={{
          background: "white",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
        gap="12px"
      >
        <CTypeMap defaultParlor={defaultCrema} address={defaultCrema.address} />
      </Stack>

      <Stack
        margin="20px 12px"
        padding="24px 16px 10px"
        style={{
          background: "white",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
        gap="12px"
      >
        <h3 className="button_16_sb">부산 경남 울산 국가유공자 화장장</h3>
        {regionCrematoriums.map((res) => (
          <BTypeCardView
            descriptions={["국가유공자 무료"]}
            mainColor={regionColor}
            key={res.companyname}
            name={res.companyname}
            href={`/national/free-crema/${res.companyname} 소개/`}
            imgSrc={res.images[0].url ? res.images[0].url : ""}
            event={{
              domain: "gyeongsang",
              path: "nat_fr-crema",
              trigger_and_target: "uv_fr_crema_detail",
            }}
          />
        ))}
      </Stack>
    </Stack>
  );
}
